import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { AssetService } from '../../models/assetservice.class';
import { BridgeNode } from '../../models/bridgeNode.interface';
import { AlertError } from '../../store/actions';
import { Service } from '../../store/states';
import { BridgeService } from '../bridge/bridge.service';
import { CommonUtilsService } from '../commonutils/common-utils.service';
import { RootScopeService } from '../rootscope/rootscope.service';
import { StoreService } from '../store/store.service';
import { UtilsService } from '../utils/utils.service';

@Injectable({
    providedIn: 'root',
})
export class SubscriptionsService {
    constructor(
        private _bridge: BridgeService,
        private _utils: UtilsService,
        protected R: RootScopeService,
        private _store: StoreService,
        private store$: Store
    ) {}

    /**
     * Method to fetch subscribe services
     * @param data contains organizationId
     * @param callbacks contains success and failure calls
     */
    searchSubscribedServicesByOrganizationId = async (
        data: {
            organizationId: any;
            noAlerts?: boolean;
        },
        callbacks?: {
            successCallback: (services: AssetService[]) => void;
            failureCallback: (res) => void;
        }
    ) => {
        const appsResponse: any = await this._store.publicScope.fetchValues(
            () => {
                return new Promise(resolve => {
                    this._bridge.searchSubscribedServicesByOrganizationId(
                        data,
                        res => {
                            resolve(res?.response || []);
                        },
                        res => {
                            callbacks.failureCallback(res);
                        }
                    );
                });
            },
            'searchApps',
            data.organizationId
        );
        callbacks.successCallback(appsResponse?.['searchApps']?.[data.organizationId] || []);
    };

    /**
     * Method to fetch subscribe services
     * @param data contains organizationId
     * @param callbacks contains success and failure calls
     */
    getSubscribedServices = async (
        data?: {
            noAlerts?: boolean;
        },
        callbacks?: {
            successCallback: (
                services: {
                    id?: string;
                    createdDate?: string;
                    lastModifiedDate?: string;
                    name?: string;
                    restApiName?: string;
                    serviceId?: string;
                    assetMetaUId?: string;
                    assetType?: string;
                    filableReport?: boolean;
                }[]
            ) => void;
            failureCallback: (res) => void;
        },
        byPass?: boolean
    ) => {
        byPass && this._store.privateScope.clearValue('services');
        const appsResponse: any = await this._store.privateScope.fetchValues(() => {
            return new Promise(async (resolve, reject) => {
                this._bridge.getSubscribedServices(
                    data,
                    res => {
                        const apps = res?.response || [];
                        apps.forEach((app: any) => {
                            app.originalLastModifiedDate = app.lastModifiedDate;
                            app.lastModifiedDate = CommonUtilsService.transformDateToLocale(
                                app.originalLastModifiedDate,
                                'YYYY-MM-DDTHH:MM:SSZ',
                                'DD-MM-YYYY HH:MM:SS AM',
                                false
                            );
                            // tagName to tagKey migration: API now returns tagKey instead of tagName
                            app.assetType = app.serviceType;
                            if (app.assetType === 'PACKAGE') {
                                if (app.displayName.includes('--')) {
                                    app.displayName = app.displayName.substring(0, app.displayName.indexOf('--'));
                                }
                            }
                            delete app.serviceType;
                        });
                        resolve(apps);
                    },
                    res => {
                        callbacks?.failureCallback?.(res);
                        reject(res?.msg);
                    }
                );
            });
        }, 'services');
        if (callbacks?.successCallback) {
            callbacks.successCallback(appsResponse?.['services'] || []);
        } else {
            return appsResponse?.['services'] || [];
        }
    };

    /**
     * Fetching enreconcile services
     * @param data contains organizationId
     * @param callbacks contains success and failure calls
     */
    getReconciliationServices = (byPass?: boolean, noAlerts?: boolean) => {
        return new Promise<AssetService[]>(async appsResolve => {
            byPass && this._store.privateScope.clearValue('reconciliationServices');
            const appsResponse: any = await this._store.privateScope.fetchValues(() => {
                return new Promise(async (resolve, reject) => {
                    this._bridge.getReconciliationServices(
                        {
                            noAlerts,
                        },
                        res => {
                            const apps = res?.response || [];
                            apps.forEach(app => {
                                app.originalLastModifiedDate = app.lastModifiedDate;
                                app.lastModifiedDate = CommonUtilsService.transformDateToLocale(
                                    app.originalLastModifiedDate,
                                    'YYYY-MM-DDTHH:MM:SSZ',
                                    'DD-MM-YYYY HH:MM:SS AM',
                                    false
                                );
                            });
                            resolve(apps);
                        },
                        res => {
                            this._utils.alertError(res?.msg || 'Failed to get reconciliation services');
                            reject();
                        }
                    );
                });
            }, 'reconciliationServices');
            appsResolve(appsResponse?.['reconciliationServices'] || []);
        });
    };

    /**
     * Method to fetch subscribe services
     * @param data contains organizationId
     * @param callbacks contains success and failure calls
     */
    getCollaboratedApps = async (
        data?: {
            noAlerts?: boolean;
        },
        callbacks?: {
            successCallback: (
                services: {
                    id?: string;
                    createdDate?: string;
                    lastModifiedDate?: string;
                    name?: string;
                    restApiName?: string;
                    serviceId?: string;
                    assetMetaUId?: string;
                    assetType?: string;
                    filableReport?: boolean;
                }[]
            ) => void;
            failureCallback?: (res) => void;
        },
        byPass?: boolean
    ) => {
        byPass && this._store.privateScope.clearValue('services');
        const appsResponse: any = await this._store.privateScope.fetchValues(() => {
            return new Promise(async (resolve, reject) => {
                this._bridge.getCollaboratedApps(
                    data,
                    res => {
                        const apps = res?.response?.services || [];
                        apps?.forEach(app => {
                            app.originalLastModifiedDate = app.lastModifiedDate;
                            app.lastModifiedDate = CommonUtilsService.transformDateToLocale(
                                app.originalLastModifiedDate,
                                'YYYY-MM-DDTHH:MM:SSZ',
                                'DD-MM-YYYY HH:MM:SS AM',
                                false
                            );
                        });
                        resolve(apps);
                    },
                    res => {
                        callbacks?.failureCallback?.(res);
                        reject();
                    }
                );
            });
        }, 'collaboratedServices');
        callbacks.successCallback(appsResponse?.['collaboratedServices'] || []);
    };

    /**
     * Method to fetch services underlying bridge
     * @param data contains assetservice properties
     * @param callbacks contains success and failure calls
     */
    getBridgeNodes = (
        data: { assetMetaUId: string },
        callbacks?: {
            successCallback: (bridgeNodes: {
                [property: string]: {
                    name?: string;
                    id?: string;
                    version?: string;
                    componentType?: string;
                    level?: string;
                    restApiName?: string;
                    nextNodes?: string[];
                };
            }) => void;
            failureCallback: (res) => void;
        }
    ) => {
        if (this.R.bridgeNodes[data.assetMetaUId]) {
            callbacks.successCallback(this.R.bridgeNodes[data.assetMetaUId] as any);
        } else {
            this._bridge.getBridgeNodes(
                data,
                res => {
                    this.R.bridgeNodes[data.assetMetaUId] = res && res.response;
                    callbacks.successCallback(this.R.bridgeNodes[data.assetMetaUId] as any);
                },
                res => {
                    callbacks.failureCallback(res);
                }
            );
        }
    };

    /**
     * Method to fetch services underlying bridge (used by NgRx effect).
     * Routes through the shared publicScope cache so concurrent calls are deduplicated.
     * @param assetMetaUId is asset id
     */
    fetchBridgeNodes = (assetMetaUId: string): Observable<{ [property: string]: BridgeNode }> => {
        return new Observable(observer => {
            this.fetchCachedBridgeNodes(assetMetaUId)
                .then(nodes => {
                    observer.next(nodes);
                    observer.complete();
                })
                .catch(e => {
                    observer.error(e?.msg || 'Failed to get bridge nodes');
                    observer.complete();
                });
        });
    };

    /**
     * Shared cached helper — all callers (fetchBridgeNodes + fetchServicesUnderlyingBridge)
     * resolve through publicScope.fetchValues so a single network call services
     * every concurrent request for the same assetMetaUId.
     */
    private fetchCachedBridgeNodes = async (assetMetaUId: string, custom?: any): Promise<{ [property: string]: BridgeNode }> => {
        const appsResponse: any = await this._store.publicScope.fetchValues(
            () => {
                return new Promise<any>(async resolve => {
                    this._bridge
                        .fetchServicesUnderlyingBridge(assetMetaUId, custom)
                        .then(async nodes => {
                            await this.fetchServiceIdOfNodes(nodes);
                            resolve(nodes);
                        })
                        .catch(e => {
                            this._utils.alertError(e?.msg || 'Failed to get bridge nodes');
                            resolve(undefined);
                        });
                });
            },
            'bridgeNodes',
            assetMetaUId
        );
        return appsResponse?.['bridgeNodes']?.[assetMetaUId];
    };

    /**
     * Method to fetch services underlying bridge
     * @param assetMetaUId is asset id
     * @param callbacks contains success and failure calls
     */
    fetchServicesUnderlyingBridge = async (
        assetMetaUId: string,
        callbacks?: {
            successCallback: (bridgeNodes: {
                [property: string]: {
                    componentType?: string;
                    id?: string;
                    level?: number;
                    name?: string;
                    nextNodes?: string[];
                    restApiName?: string;
                    version?: string;
                    displayName?: string;
                };
            }) => void;
            failureCallback: (res) => void;
        },
        custom?: any,
        _ngRxStore = false
    ) => {
        const response = await this.fetchCachedBridgeNodes(assetMetaUId, custom);
        if (callbacks?.successCallback) {
            callbacks.successCallback(response);
        } else {
            return response;
        }
    };

    private fetchServiceIdOfNodes = (bridgeNodes: {
        [property: string]: {
            componentType?: string;
            id?: string;
            level?: number;
            name?: string;
            nextNodes?: string[];
            restApiName?: string;
            version?: string;
        };
    }) => {
        const promises = [];
        Object.keys(bridgeNodes).forEach(nodeId => {
            promises.push(this.getServiceIdOfNode(bridgeNodes[nodeId]));
        });
        return Promise.all(promises);
    };

    private getServiceIdOfNode = async (node: {
        componentType?: string;
        id?: string;
        level?: number;
        name?: string;
        nextNodes?: string[];
        restApiName?: string;
        version?: string;
    }) => {
        const serviceDetails = await this.getServiceDetails(node.id);
        node['serviceId'] = serviceDetails?.serviceId;
    };

    /**
     * Method to fetch services underlying bridge using serviceId
     * @param data contains assetservice properties
     * @param callbacks contains success and failure calls
     */
    fetchAppsUnderlyingBridge = (
        data: {
            serviceId: string;
            showLoader?: boolean;
        },
        callbacks?: {
            successCallback: (res) => void;
            failureCallback: (res) => void;
        }
    ) => {
        this._bridge.fetchAppsUnderlyingBridge(
            data,
            res => {
                callbacks.successCallback(res);
            },
            res => {
                callbacks.failureCallback(res);
            }
        );
    };

    /**
     * Method to index the app
     * @param data contains assetservice properties
     * @param callbacks contains success and failure calls
     */
    indexAppOrMasterData = (
        data: {
            orgId: string;
            restApiName: string;
            fromDate?: number;
            toDate?: number;
        },
        callbacks?: {
            successCallback: (res) => void;
            failureCallback: (res) => void;
        }
    ) => {
        this._bridge.indexAppOrMasterData(
            data,
            res => {
                callbacks.successCallback(res);
            },
            res => {
                callbacks.failureCallback(res);
            }
        );
    };

    /**
     * Method to get services
     * @param callbacks contains success and failure calls
     */
    getServiceDetails = async (
        assetMetaUId: string,
        _ngRxStore = false
    ): Promise<{
        id?: string;
        repositoryId?: string;
        metadataType?: string;
        metadataName?: string;
        metadataVersion?: string;
        metadataId?: string;
        tableName?: string;
        publicationState?: string;
        serviceId?: any;
        migrated?: boolean;
    }> => {
        const appsResponse: any = await this._store.publicScope.fetchValues(
            () => {
                const promise = this._bridge.getServiceDetails(assetMetaUId);
                promise.catch(e => this._utils.alertError(e?.msg || 'Failed to get service details'));
                return promise;
            },
            'serviceDetails',
            assetMetaUId
        );
        return appsResponse?.['serviceDetails']?.[assetMetaUId] || {};
    };

    /**
     * Method to subscribe to services
     * @param data contains organizationId and payload
     * @param callbacks contains success and failure calls
     */
    subscribeToServices = (data: { organizationIds: any[]; serviceIds: any[] }) => {
        return new Observable(observer => {
            this._bridge.subscribeToServices(
                data,
                res => {
                    observer.next(res?.response);
                    observer.complete();
                },
                res => {
                    observer.error(res?.msg);
                    observer.complete();
                }
            );
        });
    };

    /**
     * Method to validate & subscribe to connector
     * @param data contains organizationId and payload
     * @param callbacks contains success and failure calls
     */
    validateAndsubscribeToConnector = (data: {
        organizationIds: any[];
        resourceName: string;
        requiredFields?: { [key: string]: string };
    }) => {
        return new Observable<any>(observer => {
            this._bridge.validateAndsubscribeToConnector(
                data,
                res => {
                    observer.next(res?.response);
                    observer.complete();
                },
                res => {
                    observer.error(res?.msg);
                    observer.complete();
                }
            );
        });
    };

    /**
     * Method to subscribe to services
     * @param data contains organizationId and payload
     * @param callbacks contains success and failure calls
     */
    updateSubscribedServices = (data: { subscriptionIds: any[]; status: string }) => {
        return new Observable(observer => {
            this._bridge.updateSubscribedServices(
                data,
                res => {
                    observer.next(res);
                    observer.complete();
                },
                res => {
                    observer.error(res?.msg);
                    observer.complete();
                }
            );
        });
    };

    /**
     * Method to fetch subscribed widgets
     * @param data contains organizationId
     * @param callbacks contains success and failure calls
     */
    fetchSubscribedWidgets = (
        data: {
            organizationId: string;
        },
        callbacks?: {
            successCallback: (res) => void;
            failureCallback: (res) => void;
        }
    ) => {
        this._bridge.fetchSubscribedWidgets(
            data,
            res => {
                callbacks.successCallback(res);
            },
            res => {
                callbacks.failureCallback(res);
            }
        );
    };

    getAllServices = (serviceName?: string, considerGlobalRepository?: boolean): Observable<Service[]> => {
        return new Observable(observer => {
            this._bridge.getAssets(
                serviceName,
                considerGlobalRepository,
                res => {
                    const services = serviceName ? [res?.response] : res?.response?.items || [];
                    services.forEach(service => {
                        service.assetType = service.serviceType;
                    });
                    observer.next(
                        services.sort((a, b) => {
                            const aName = a.displayName || a.itemName;
                            const bName = b.displayName || b.itemName;
                            if (aName > bName) {
                                return 1;
                            } else if (aName < bName) {
                                return -1;
                            } else {
                                return 0;
                            }
                        })
                    );
                    observer.complete();
                },
                res => {
                    observer.error(res?.msg || 'Failed to get services');
                }
            );
        });
    };

    public getAllServicesOfCurrentProvider = () => {
        return new Observable<Service[]>(observer => {
            this._bridge.getAllServicesOfCurrentProvider(
                res => {
                    let services = res?.response?.items || [];
                    services.forEach(service => {
                        service.assetType = service.serviceType;
                    });
                    services = services.sort((a, b) => {
                        const aName = a.displayName || a.itemName;
                        const bName = b.displayName || b.itemName;
                        if (aName > bName) {
                            return 1;
                        } else if (aName < bName) {
                            return -1;
                        } else {
                            return 0;
                        }
                    });
                    observer.next(services);
                    observer.complete();
                },
                res => {
                    this.store$.dispatch(
                        AlertError({
                            message: res?.msg || 'Failed to get services',
                        })
                    );
                    observer.error(res?.msg || 'Failed to get services');
                    observer.complete();
                }
            );
        });
    };

    public getSubcriptionServices = (data: {
        pageIndex: number;
        size: number;
        status: string;
        orgIdArray: any[];
        serviceIdArray?: any[];
        noVariableLoading?: boolean;
    }) => {
        return new Observable<any>(observer => {
            this._bridge.getAssetsByStatus(
                data,
                res => {
                    const services = res?.response?.items?.reduce((subscriptions, service) => {
                        service.displayName = service.displayName || service.serviceName || service.itemName;
                        service.assetType = service.serviceType;
                        subscriptions.push(service);
                        return subscriptions;
                    }, []);
                    observer.next({
                        services,
                        count: res?.response?.totalCount,
                    });
                    observer.complete();
                },
                res => {
                    observer.error(res?.msg);
                    observer.complete();
                }
            );
        });
    };

    public getServicesByStatus = (data: {
        pageIndex: number;
        size: number;
        status: string;
        orgIdArray: any[];
        noVariableLoading?: boolean;
    }) => {
        return this.getServicesByStatusWithPagination(data);
    };
    private getServicesByStatusWithPagination = (data: {
        pageIndex: number;
        size: number;
        status: string;
        orgIdArray: any[];
        noVariableLoading?: boolean;
    }): Promise<Service[]> => {
        return new Promise(resolve => {
            this._bridge.getAssetsByStatus(
                data,
                async res => {
                    let services = res?.response?.items.reduce((subscriptions, service) => {
                        service.displayName = service.displayName || service.serviceName || service.itemName;
                        service.assetType = service.serviceType;
                        subscriptions.push(service);
                        return subscriptions;
                    }, []);
                    if (data.size === services.length) {
                        data.pageIndex += 1;
                        services = [...services, ...(await this.getServicesByStatusWithPagination(data))];
                    }
                    resolve(
                        services.sort((a, b) => {
                            const aName = a.displayName || a.itemName;
                            const bName = b.displayName || b.itemName;
                            if (aName > bName) {
                                return 1;
                            } else if (aName < bName) {
                                return -1;
                            } else {
                                return 0;
                            }
                        })
                    );
                },
                res => {
                    this.store$.dispatch(
                        AlertError({
                            message: res?.msg,
                        })
                    );
                    resolve([]);
                }
            );
        });
    };

    /**
     * Method to fetch subscribed Assets
     * @param callbacks contains success and failure calls
     */
    subscribedAssetsCount = (data: {
        organizationId: any;
        subscriptionType: string;
        status?: string;
        orgIdArray?: any[];
        itemIdArray?: any[];
    }) => {
        return new Observable(observer => {
            this._bridge.subscribedAssetsCount(
                data,
                res => {
                    observer.next(res?.response?.count || 0);
                    observer.complete();
                },
                res => {
                    observer.error(res?.msg);
                    observer.complete();
                }
            );
        });
    };

    /**
     * Method to get widgets
     * @param callbacks contains success and failure calls
     */
    searchAllOrganizations = (data: { [property: string]: string | boolean }) => {
        return new Observable<any[]>(observer => {
            this._bridge.searchAllOrganizations(
                data,
                res => {
                    observer.next(res);
                    observer.complete();
                },
                res => {
                    observer.error(res);
                    observer.complete();
                }
            );
        });
    };

    /**
     * Method to get recently accessed services
     * @param callbacks contains success and failure calls
     */
    getRecentlyAccessedServices = (callbacks?: { successCallback: (res) => void; failureCallback: (res) => void }) => {
        this._bridge.getRecentlyAccessedServices(
            res => {
                callbacks.successCallback(res);
            },
            res => {
                callbacks.failureCallback(res);
            }
        );
    };

    /**
     * Method to fetch bridge metadata
     * @param data contains assetservice properties
     * @param callbacks contains success and failure calls
     */
    getBridgeMetaData = (
        data: { assetMetaUId: string; hideAlerts?: boolean },
        callbacks?: {
            successCallback: (res) => void;
            failureCallback: (res) => void;
        }
    ) => {
        this._bridge.getBridgeMetaData(
            data,
            res => {
                callbacks.successCallback((res && res.response) || {});
            },
            res => {
                callbacks.failureCallback(res);
            }
        );
    };

    getAssetsForSubscriptionApprovalFilters = async (): Promise<Service[]> => {
        return new Promise(resolve => {
            this._bridge.getAssetsForSubscriptionApprovalFilters(
                res => {
                    resolve(res?.response?.items || []);
                },
                res => {
                    this.store$.dispatch(
                        AlertError({
                            message: res?.msg || 'Failed to get services',
                        })
                    );
                    resolve([]);
                }
            );
        });
    };

    unsubscribeFromService = (subscriptionId: string): Observable<string> => {
        return new Observable(observer => {
            this._bridge.deleteSubscription(
                subscriptionId,
                res => {
                    observer.next(res.msg);
                    observer.complete();
                },
                res => {
                    observer.error(res);
                    observer.complete();
                }
            );
        });
    };
}
